/* eslint-disable max-lines */
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { authenticatedFetch } from "@lumi/data";
import { Plus, RefreshCw, Settings, Trash2 } from "lucide-react";
import { useState, type ReactElement } from "react";
import { useAuth } from "../../../auth/AuthContext";
import { createRefreshCallback } from "../../../auth/refreshCallback";
import { Button } from "../../../shared/components/ui/button";
import { cn } from "../../../shared/components/ui/cn";
import { useToast } from "../../../shared/hooks/use-toast";
import type { CollectionDetails, CollectionItem, StatusMessage } from "../types/collections";
import { AddProviderDialog } from "./AddProviderDialog";
import { AddTitleDialog } from "./AddTitleDialog";
import { CollectionConfigEditor } from "./CollectionConfigEditor";
import { CreateCollectionConfigEditor } from "./CreateCollectionConfigEditor";
import { DraggableCollectionItem } from "./DraggableCollectionItem";
import { MapRevealConfigEditor } from "./MapRevealConfigEditor";

/** Empty collection placeholder */
function EmptyCollectionPlaceholder({
  onAddClick,
  isProviderCollection,
}: {
  readonly onAddClick: () => void;
  readonly isProviderCollection: boolean;
}): ReactElement {
  return (
    <div className="border border-neutral-800 rounded-lg p-12 text-center">
      <Trash2 className="mx-auto mb-4 text-neutral-600" size={48} />
      <p className="text-neutral-400 mb-4">No items in this collection</p>
      <Button onClick={onAddClick} variant="ghost-sm">
        <Plus className="mr-2" size={16} />
        {isProviderCollection ? "Add First Provider" : "Add First Title"}
      </Button>
    </div>
  );
}

/** Provider items list for PROVIDER template collections */
function ProviderItemsList({
  items,
  onRemoveItem,
}: {
  readonly items: readonly CollectionItem[];
  readonly onRemoveItem: (id: string, isProvider: boolean) => void | Promise<void>;
}): ReactElement {
  return (
    <div className="space-y-2">
      {items.map((item, index) => (
        <div
          key={item.provider?.id ?? index}
          className="flex items-center gap-4 p-4 bg-none border-b border-b-neutral-800"
        >
          <div className="w-12 h-12 rounded-lg bg-neutral-800 flex items-center justify-center overflow-hidden flex-shrink-0">
            {(item.provider?.logoSqUrl ?? item.provider?.logoUrl) ? (
              <img
                src={item.provider?.logoSqUrl ?? item.provider?.logoUrl ?? ""}
                alt={item.provider?.displayName}
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-neutral-500 text-lg font-medium">
                {item.provider?.displayName?.charAt(0).toUpperCase()}
              </span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h5 className="text-white font-medium truncate">{item.provider?.displayName}</h5>
              {item.provider?.verified && (
                <span className="text-xs bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded">Verified</span>
              )}
            </div>
            <p className="text-sm text-neutral-500">@{item.provider?.slug}</p>
          </div>
          <Button
            onClick={() => {
              onRemoveItem(item.provider?.id ?? "", true)?.catch(console.error);
            }}
            variant="ghost-sm"
            size="sm"
            className="flex-shrink-0 text-white/50 hover:text-red-500 hover:bg-red-900/20"
          >
            <Trash2 size={16} />
          </Button>
        </div>
      ))}
    </div>
  );
}

/** Title items list with drag-and-drop reordering */
function TitleItemsList({
  items,
  collectionKey,
  sensors,
  onDragEnd,
  onRemoveTitle,
}: {
  readonly items: readonly CollectionItem[];
  readonly collectionKey: string;
  readonly sensors: ReturnType<typeof useSensors>;
  readonly onDragEnd: (event: DragEndEvent) => void | Promise<void>;
  readonly onRemoveTitle: (id: string) => void | Promise<void>;
}): ReactElement {
  const titleItems = items.filter((item) => item.title);
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={(e) => {
        onDragEnd(e)?.catch(console.error);
      }}
    >
      <SortableContext items={titleItems.map((item) => item.title!.id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-2">
          {titleItems.map((item, index) => (
            <DraggableCollectionItem
              key={item.title!.id}
              item={{ ...item, title: item.title! }}
              index={index}
              onRemove={(id) => {
                onRemoveTitle(id)?.catch(console.error);
              }}
              collectionKey={collectionKey}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

interface CollectionDetailsViewProps {
  readonly details: CollectionDetails | null;
  readonly isLoading: boolean;
  readonly collectionKey: string | null;
  readonly isCreatingNew?: boolean;
  readonly onRefresh: () => void;
  readonly onCreateSuccess?: (createdKey: string) => void;
  readonly onCreateCancel?: () => void;
}

/**
 * Spacing between rank values when reordering collection items
 * Higher values leave more room for manual adjustments
 */
const RANK_SPACING = 1000;

/**
 * CollectionDetailsView - Enhanced collection management with drag-and-drop and inline editing
 *
 * This component supports three modes:
 * 1. Creating new collection (isCreatingNew=true) - shows CreateCollectionConfigEditor
 * 2. Editing existing collection config (isEditingConfig=true) - shows CollectionConfigEditor
 * 3. Managing collection items (default) - shows items list with drag-and-drop
 *
 * Special case: MAP_REVEAL template always shows MapRevealConfigEditor instead of items list
 */
// eslint-disable-next-line max-lines-per-function
export function CollectionDetailsView({
  details,
  isLoading,
  collectionKey,
  isCreatingNew = false,
  onRefresh,
  onCreateSuccess,
  onCreateCancel,
}: CollectionDetailsViewProps): ReactElement {
  const { toast } = useToast();
  const { accessToken, refreshAuth } = useAuth();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [isEditingConfig, setIsEditingConfig] = useState(false);
  const [message, setMessage] = useState<StatusMessage | null>(null);

  const API_BASE = import.meta.env.VITE_API_BASE_URL || "http://localhost:3333";

  // Determine if this is a provider collection
  const isProviderCollection = details?.collection.template === "PROVIDER";

  // Set up drag-and-drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleSync = async () => {
    if (!accessToken) return;

    setSyncing(true);
    setMessage(null);

    try {
      const response = await authenticatedFetch<{ message: string; added: number; removed: number }>(
        `${API_BASE}/v1/admin/sync-new-releases`,
        { method: "POST" },
        accessToken,
        createRefreshCallback(refreshAuth),
      );

      toast({
        description: `Synced! Added: ${response.added}, Removed: ${response.removed}`,
      });
      onRefresh();
    } catch (err) {
      console.error("❌ Sync failed:", err);
      toast({
        description: "Failed to sync collection. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleRemoveItem = async (itemId: string, isProvider: boolean) => {
    if (!collectionKey || !accessToken) return;

    setMessage(null);

    try {
      await authenticatedFetch(
        `${API_BASE}/v1/admin/collections/${collectionKey}/items/${itemId}`,
        { method: "DELETE" },
        accessToken,
        createRefreshCallback(refreshAuth),
      );

      toast({
        description: isProvider ? "Provider removed from collection" : "Title removed from collection",
      });
      onRefresh();
    } catch (err) {
      console.error("❌ Failed to remove item:", err);
      toast({
        description: "Failed to remove item. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Legacy wrapper for backward compatibility with DraggableCollectionItem
  const handleRemoveTitle = async (titleId: string) => {
    return handleRemoveItem(titleId, false);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || !details || active.id === over.id || !accessToken) return;

    // For provider collections, skip drag-drop (not implemented yet)
    if (isProviderCollection) return;

    // Filter to title items only for reordering
    const titleItems = details.items.filter((item) => item.title != null);
    const oldIndex = titleItems.findIndex((item) => item.title?.id === active.id);
    const newIndex = titleItems.findIndex((item) => item.title?.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    // Optimistic update: reorder immediately in UI
    const reorderedItems = [...titleItems];
    const [movedItem] = reorderedItems.splice(oldIndex, 1);
    reorderedItems.splice(newIndex, 0, movedItem);

    // Show reordering feedback immediately
    setReordering(true);
    setMessage(null);

    try {
      // Assign new ranks based on position (higher index = higher rank for desc ordering)
      const updates = reorderedItems.map((item, index) => ({
        titleId: item.title?.id,
        rank: (reorderedItems.length - index) * RANK_SPACING, // Descending ranks
      }));

      // Send bulk update to API
      await authenticatedFetch(
        `${API_BASE}/v1/admin/collections/${collectionKey}/items/reorder`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ items: updates }),
        },
        accessToken,
        createRefreshCallback(refreshAuth),
      );

      toast({
        description: "Collection reordered successfully",
      });
      onRefresh();
    } catch (err) {
      console.error("❌ Failed to reorder collection:", err);
      toast({
        description: "Failed to reorder collection. Changes reverted.",
        variant: "destructive",
      });
      // Rollback would happen via onRefresh() fetching original order
      onRefresh();
    } finally {
      setReordering(false);
    }
  };

  /** Renders the appropriate collection items view based on state */
  const renderCollectionItems = (): ReactElement => {
    if (!details || details.items.length === 0) {
      return (
        <EmptyCollectionPlaceholder
          onAddClick={() => setShowAddDialog(true)}
          isProviderCollection={isProviderCollection}
        />
      );
    }

    if (isProviderCollection) {
      return <ProviderItemsList items={details.items} onRemoveItem={handleRemoveItem} />;
    }

    return (
      <TitleItemsList
        items={details.items}
        collectionKey={details.collection.key}
        sensors={sensors}
        onDragEnd={handleDragEnd}
        onRemoveTitle={handleRemoveTitle}
      />
    );
  };

  if (!collectionKey) {
    return (
      <div className="bg-transparent border border-neutral-800 rounded-lg p-12 text-center space-y-6">
        <div className="space-y-3">
          <h3 className="text-xl font-semibold text-white">Select a Collection</h3>
          <p className="text-neutral-400 max-w-md mx-auto">
            Choose a collection from the sidebar to view and manage its content, or create a new one to get started.
          </p>
        </div>
      </div>
    );
  }

  // Handle creating new collection
  if (isCreatingNew) {
    return (
      <CreateCollectionConfigEditor
        onSuccess={(createdKey) => {
          if (onCreateSuccess) {
            onCreateSuccess(createdKey);
          }
        }}
        onCancel={() => {
          if (onCreateCancel) {
            onCreateCancel();
          }
        }}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-lumi-accent"></div>
      </div>
    );
  }

  if (!details) {
    return <></>;
  }

  // For MAP_REVEAL collections, show the config editor instead of items list
  if (details.collection.template === "MAP_REVEAL") {
    return <MapRevealConfigEditor collection={details.collection} onSuccess={onRefresh} />;
  }

  // Handle editing collection config inline
  if (isEditingConfig) {
    return (
      <CollectionConfigEditor
        collection={details.collection}
        onSuccess={() => {
          if (document.startViewTransition) {
            document.startViewTransition(() => {
              setIsEditingConfig(false);
              onRefresh();
            });
          } else {
            setIsEditingConfig(false);
            onRefresh();
          }
        }}
        onCancel={() => {
          if (document.startViewTransition) {
            document.startViewTransition(() => {
              setIsEditingConfig(false);
            });
          } else {
            setIsEditingConfig(false);
          }
        }}
      />
    );
  }

  return (
    <>
      {/* Items List */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold text-white">Items</h3>
          <div className="flex items-center gap-2">
            {reordering && <span className="text-sm text-neutral-500 mr-2">Saving order...</span>}
            <Button
              onClick={() => {
                if (document.startViewTransition) {
                  document.startViewTransition(() => {
                    setIsEditingConfig(true);
                  });
                } else {
                  setIsEditingConfig(true);
                }
              }}
              variant="ghost-sm"
              size="sm"
              title="Edit collection settings"
            >
              <Settings className="mr-2" size={16} />
              Settings
            </Button>
            {collectionKey === "new-releases" && (
              <Button onClick={() => void handleSync()} disabled={syncing} variant="ghost-sm" size="sm">
                <RefreshCw className={`mr-2 ${syncing ? "animate-spin" : ""}`} size={16} />
                {syncing ? "Syncing..." : "Sync"}
              </Button>
            )}
            <Button onClick={() => setShowAddDialog(true)} variant="ghost-sm" size="sm">
              <Plus className="mr-2" size={16} />
              {isProviderCollection ? "Add Provider" : "Add Title"}
            </Button>
          </div>
        </div>

        {message && (
          <div
            className={cn(
              "p-3 border rounded",
              message.type === "success" ? "bg-green-900/20 border-green-800" : "bg-red-900/20 border-red-800",
            )}
          >
            <p className={cn("text-sm", message.type === "success" ? "text-green-400" : "text-red-400")}>
              {message.text}
            </p>
          </div>
        )}

        {renderCollectionItems()}
      </div>

      {/* Add Title Dialog */}
      {!isProviderCollection && (
        <AddTitleDialog
          isOpen={showAddDialog}
          collectionKey={collectionKey}
          onClose={() => setShowAddDialog(false)}
          onSuccess={onRefresh}
        />
      )}

      {/* Add Provider Dialog */}
      {isProviderCollection && (
        <AddProviderDialog
          isOpen={showAddDialog}
          collectionKey={collectionKey}
          onClose={() => setShowAddDialog(false)}
          onSuccess={onRefresh}
        />
      )}
    </>
  );
}
